// Handling profile configuration — spec §2.5, chapter 6. Validated with Zod on
// publish; stored as JSON in handling_profile.config. Every field has a default so an
// instance works with the shipped profiles untouched (D6-7).
import { z } from "zod";
import { ContainerIdentityScheme, IdentificationPolicy } from "../identity/scheme.js";
export const AttributeRequirement = z.enum(["required", "optional", "forbidden"]);
export const StorageClass = z.enum(["ambient", "chilled", "frozen"]);
export const AllocationOrder = z.enum(["fefo", "fifo"]);
export const EquivalenceRule = z.discriminatedUnion("by", [
    z.object({ by: z.literal("best-before") }),
    z.object({ by: z.literal("expiry-within-days"), days: z.number().int().positive() }),
    z.object({ by: z.literal("lot") }),
    z.object({ by: z.literal("receipt") }),
]);
export const SplitPolicy = z.enum(["inherit", "serial", "derived-lot", "new-lot"]);
export const GtinPolicy = z.enum(["parent-gtin", "tenant-variable-measure-gtin"]);
export const EnforcementLevel = z.enum(["enforce", "override-with-approval", "override-with-reason", "free"]);
export const HandlingProfileConfig = z
    .object({
    capture: z
        .object({
        lot: AttributeRequirement.default("required"),
        bestBefore: AttributeRequirement.default("optional"),
        expiry: AttributeRequirement.default("optional"),
        productionDate: AttributeRequirement.default("optional"),
        serial: AttributeRequirement.default("optional"),
        netContent: AttributeRequirement.default("optional"),
        temperature: AttributeRequirement.default("forbidden"),
        photo: AttributeRequirement.default("optional"),
        /** Accept a logistic unit's declared contents (despatch advice) as inferred until scanned. */
        acceptInferredContents: z.boolean().default(false),
        /** A code lacking what the identity scheme needs (§3.3 scan assembly). */
        partialCodes: z.enum(["assemble-required", "accept-alone", "reject"]).default("assemble-required"),
    })
        .default({}),
    identification: z
        .object({
        scheme: ContainerIdentityScheme.default("gtin+lot"),
        policy: IdentificationPolicy.default("supplier-identity-as-is"),
        /** What the product is handled as (owner 2026-11-02/03, non-batch products): "container" — every
         *  carton is a container with an identity (the label's GTIN + lot, serial …); "bulk" — a quantity
         *  of the product with no identity at all: one row per product and location, received, moved,
         *  counted and picked by a typed quantity, never labelled, reported to the ERP per order line. */
        unit: z.enum(["container", "bulk"]).default("container"),
        /** Under lot-as-instance: when a second container shares the identity. */
        onCollision: z.enum(["count", "ask-serial"]).default("ask-serial"),
        labelTemplate: z.string().nullable().default(null),
    })
        .default({}),
    storage: z
        .object({
        storageClass: StorageClass.nullable().default(null),
        segregationGroup: z.string().nullable().default(null),
        putAwayStrategy: z.enum(["nearest-empty", "same-lot-consolidation", "fixed-bin", "zone-by-role"]).default("nearest-empty"),
        constraintViolation: EnforcementLevel.default("override-with-reason"),
    })
        .default({}),
    allocation: z
        .object({
        order: AllocationOrder.default("fefo"),
        equivalence: EquivalenceRule.default({ by: "best-before" }),
        singleLotPerLine: z.boolean().default(false),
        preferWholePallets: z.boolean().default(true),
        allowBreakingPallets: z.boolean().default(true),
        outOfOrderPick: EnforcementLevel.default("override-with-approval"),
    })
        .default({}),
    split: z
        .object({
        allowed: z.boolean().default(true),
        policy: SplitPolicy.default("derived-lot"),
        gtinPolicy: GtinPolicy.default("parent-gtin"),
        derivedLotPattern: z.string().default("{parent}/{n}"),
        minimumRemainder: z.number().nonnegative().default(0),
        labelTemplate: z.string().nullable().default(null),
    })
        .default({}),
    shipping: z
        .object({
        verification: z.enum(["every-container", "sscc-when-intact"]).default("sscc-when-intact"),
    })
        .default({}),
    counting: z
        .object({
        confirmEmptyAfterLastPick: z.boolean().default(true),
        blind: z.boolean().default(true),
        adjustmentApprovalThreshold: z.number().nonnegative().nullable().default(null),
    })
        .default({}),
})
    .strict();
/** The shipped default profiles (D6-7). Instances start from these and change what differs. */
export const DEFAULT_HANDLING_PROFILES = {
    untracked: HandlingProfileConfig.parse({
        capture: { lot: "forbidden", bestBefore: "forbidden", expiry: "forbidden", netContent: "forbidden" },
        identification: { scheme: "gtin+lot", policy: "supplier-identity-as-is", unit: "bulk" },
        allocation: { order: "fifo", equivalence: { by: "receipt" } },
    }),
    "lot-tracked": HandlingProfileConfig.parse({}),
    "lot-tracked-with-dates": HandlingProfileConfig.parse({
        capture: { bestBefore: "required" },
    }),
    "lot-per-container": HandlingProfileConfig.parse({
        capture: { bestBefore: "required" },
        identification: { scheme: "gtin+lot", policy: "lot-as-instance" },
    }),
    serialised: HandlingProfileConfig.parse({
        capture: { serial: "required" },
        identification: { scheme: "gtin+serial", policy: "serialise-at-receipt" },
    }),
    "cold-chain": HandlingProfileConfig.parse({
        capture: { bestBefore: "required", temperature: "required" },
        storage: { storageClass: "frozen" },
    }),
};
//# sourceMappingURL=handling-profile.js.map